import { Component, effect, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GenericComponent } from '../generic/generic.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SidenavService } from 'src/app/shared/services/sidenav.service';
import { Store } from '@ngxs/store';
import { ConcessioncontrolState, PrepareDataFromKeys } from '../../states/concessioncontrol.state';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { CC_CONSTANTS, StrokeLineCap } from '../../assets/constants';
import {
    ApexAxisChartSeries,
    ApexDataLabels,
    ApexGrid,
    ApexLegend,
    ApexPlotOptions,
    ApexTooltip,
    ApexXAxis,
    ApexYAxis,
} from 'ng-apexcharts';
import {
    colorAec1BlueLight,
    colorAec2BlueDark,
    colorAec3Green,
    colorAec4Orange,
    colorAec5Brown,
    colorAec6Gray,
    colorAec7RedLight,
    colorAec8RedDark,
    colorAec9Cream,
    colorWhite,
    colorBlack,
} from 'src/app/shared/all.constants';
import { convertNumber, hexToRgba } from 'src/app/shared/all.helpers';
import {
    ACCOUNTING_INDICATORS_DEPRECIATION_RATE_COMPARISON_CHART,
    ACCOUNTING_INDICATORS_EVOL_FIXED_ASSETS_CHART,
    ACCOUNTING_INDICATORS_EVOL_GROSS_VALUE_CHART,
    ACCOUNTING_INDICATORS_EVOL_RATE_DEPREC_WORKS_CHART,
    ACCOUNTING_INDICATORS_GROSS_VALUE_BREAKDOWN_CHART,
    ACCOUNTING_INDICATORS_GROSS_VALUE_COMPARISON_CHART,
    ACCOUNTING_INDICATORS_KPIS,
    ACCOUNTING_INDICATORS_RATE_DEPREC_WORKS_FAMILY_CHART,
    ACCOUNTING_PROVISIONS_DISTRIBUTION_CHART,
    ACCOUNTING_REPLACEMENT_VALUE_CHART,
    ACCOUNTING_TOTAL_PROVISION_CHART,
} from '../../assets/keys';

const KEYS = [
    ACCOUNTING_INDICATORS_KPIS,
    ACCOUNTING_INDICATORS_EVOL_FIXED_ASSETS_CHART,
    ACCOUNTING_INDICATORS_GROSS_VALUE_COMPARISON_CHART,
    ACCOUNTING_INDICATORS_DEPRECIATION_RATE_COMPARISON_CHART,
    ACCOUNTING_INDICATORS_EVOL_GROSS_VALUE_CHART,
    ACCOUNTING_INDICATORS_EVOL_RATE_DEPREC_WORKS_CHART,
    ACCOUNTING_INDICATORS_RATE_DEPREC_WORKS_FAMILY_CHART,
    ACCOUNTING_REPLACEMENT_VALUE_CHART,
    ACCOUNTING_TOTAL_PROVISION_CHART,
    ACCOUNTING_PROVISIONS_DISTRIBUTION_CHART,
    ACCOUNTING_INDICATORS_GROSS_VALUE_BREAKDOWN_CHART,
];

interface KpiData {
    organizationId?: string;
    year?: string;
    error?: boolean;

    totalGrossValue?: number;
    totalGrossValueEvolution?: number;

    grossValuePerUserRatio?: number;
    grossValuePerUserRatioEvolution?: number;

    totalRenewalProvision?: number;
    totalRenewalProvisionEvolution?: number;

    overallDepreciationRate?: number;
    overallDepreciationRateEvolution?: number;
}

interface ChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=400
    totalValueNetBook?: number;
    //kpi_id=401
    totalDepreciation?: number;
    //kpi_id=399
    totalGrossValue?: number;
    error?: boolean;
}

interface ChartDataEvolVb {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=407
    valueGrossUser?: number;
    error?: boolean;
}

interface ChartDataEvolRep {
    organizationId?: string;
    year?: string;
    xYear?: number;
    // kpi_id=407
    totalRateDepreWorks?: number;
    error?: boolean;
}

interface ChartDataRateRep {
    organizationId?: string;
    year?: string;
    xYear?: number;

    id?: number;
    rateDepreciationMv?: number;
    rateDepreciationLv?: number;
    rateDepreciationSubstation?: number;
    rateDepreciationTransformer?: number;
    rateDepreciationLinky?: number;
    rateDepreciationMarket?: number;
    rateDepreciationCollectiveWorks?: number;
    rateDepreciationOtherOl?: number;
    rateDepreciationOtherOnl?: number;
    rateDepreciationUndergroundLr?: number;
    rateDepreciationBreaker?: number;
    rateDepreciationOverhead?: number;
    rateDepreciationUndergroundDi?: number;
    error?: boolean;
}

interface ComparisonChartData {
    id?: number;
    organizationId?: string;
    year?: string;
    category?: string;
    min?: number;
    max?: number;
    mean?: number;
    clientValue?: number;
    error?: boolean;
}

interface ReplacementValueChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    replacementValue?: number;
    annualReplacementCoeff?: number;
}

interface TotalProvisionChartData {
    organizationId?: string;
    year?: string;
    xYear?: number;
    totalProvision?: number;
}

interface ProvisionsDistributionChartData {
    organizationId?: string;
    year?: string;
    meters?: number;
    collectiveConnection?: number;
    individualConnection?: number;
    lv?: number;
    transformers?: number;
    posts?: number;
    mv?: number;
    others?: number;
}

interface GrossValueChartData {
    organizationId?: string;
    year?: string;
    othersNonLocated?: number;
    otherLocatedAssets?: number;
    circuitBreakers?: number;
    businessMarketMeters?: number;
    linkyMeters?: number;
    nonLocatedMetering?: number;
    aerialUndergroundDi?: number;
    aerialUndergroundLr?: number;
    aerialConnections?: number;
    ocb?: number;
    nonLocatedConnectionFacilities?: number;
    transformers?: number;
    linkyConcentrators?: number;
    lvMvSubstations?: number;
    undergroundLv?: number;
    twistedLv?: number;
    aerialLv?: number;
    undergroundMv?: number;
    aerialMv?: number;
}

@UntilDestroy()
@Component({
    selector: 'app-accounting-indicators',
    templateUrl: './accounting-indicators.component.html',
    styleUrls: ['./accounting-indicators.component.scss'],
})
export class AccountingIndicatorsComponent extends GenericComponent {
    public texts = CC_CONSTANTS.kpis.texts;

    public kpis: Signal<KpiData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_INDICATORS_KPIS),
    ) as Signal<KpiData[]>;

    public chartData: Signal<ChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_INDICATORS_EVOL_FIXED_ASSETS_CHART),
    ) as Signal<ChartData[]>;

    public chartError: boolean = true;
    public chartSeries: ApexAxisChartSeries = [];
    public dataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0, 1, 2]),
        style: this.setDataLabelsStyleColors(
            this.texts.evolutionFixedAssetsChart.totalGrossValueAxisTitle,
        ),
    };
    public xaxisCategories: ApexXAxis['categories'];
    public yaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.evolutionFixedAssetsChart.totalDepreciationAxisTitle,
            min: 0,
            title: {
                text: this.texts.evolutionFixedAssetsChart.yaxisTitle,
            },
        },
    ];
    public evolutionFixedAssetsegend: ApexLegend = {
        markers: {
            fillColors: [colorAec4Orange, colorAec1BlueLight, colorBlack],
        },
    };
    public evolutionFixedAssetsCustomTooltip: ApexTooltip['custom'];

    public chartDataEvolVb: Signal<ChartDataEvolVb[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_INDICATORS_EVOL_GROSS_VALUE_CHART),
    ) as Signal<ChartDataEvolVb[]>;

    public chartErrorEvolVb: boolean = true;
    public chartSeriesEvolVb: ApexAxisChartSeries = [];
    public dataLabelsEvolVb: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0]),
    };
    public xaxisCategoriesEvolVb: ApexXAxis['categories'];
    public yaxisEvolVb: ApexYAxis[] = [
        {
            seriesName: this.texts.grossValueEvolChart.valueGrossUserAxisTitle,
            min: 0,
            title: {
                text: this.texts.grossValueEvolChart.yaxisTitle,
            },
        },
    ];

    public chartDataEvolRep: Signal<ChartDataEvolRep[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_INDICATORS_EVOL_RATE_DEPREC_WORKS_CHART),
    ) as Signal<ChartDataEvolRep[]>;

    public chartErrorEvolRep: boolean = true;
    public chartSeriesEvolRep: ApexAxisChartSeries = [];
    public dataLabelsEvolRep: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({
            0: this.texts.rateDeprWorkEvolChart.precision,
        }),
    };
    public xaxisCategoriesEvolRep: ApexXAxis['categories'];
    public yaxisEvolRep: ApexYAxis[] = [
        {
            seriesName: this.texts.rateDeprWorkEvolChart.totalRateDepreWorksAxisTitle,
            min: 0,
        },
    ];

    public chartDataRateRep: Signal<ChartDataRateRep[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_INDICATORS_RATE_DEPREC_WORKS_FAMILY_CHART),
    ) as Signal<ChartDataRateRep[]>;

    public chartErrorRateRep: boolean = true;
    public chartSeriesRateRep: ApexAxisChartSeries = [];
    public dataLabelsRateRep: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatter([0, 1]),
        textAnchor: 'middle',
        style: {
            fontSize: '12px',
            colors: [colorAec7RedLight, colorAec8RedDark],
        },
        background: {
            enabled: true,
            foreColor: colorWhite,
            borderColor: colorWhite,
        },
    };
    public xaxisCategoriesRateRep: ApexXAxis['categories'];
    public yaxisRateWorksFamily: ApexYAxis[] = [
        {
            seriesName: this.texts.rateDeprWorksFamilyChart.yearCurrentAxisTitle,
            min: 0,
            labels: {
                formatter: (value: number) => convertNumber(value),
            },
            title: {
                text: this.texts.rateDeprWorksFamilyChart.yaxisTitle,
            },
        },
    ];
    public yaxisDeprWorkEvol: ApexYAxis[] = [
        {
            seriesName: this.texts.rateDeprWorkEvolChart.totalRateDepreWorksAxisTitle,
            min: 0,
            labels: {
                formatter: (value: number) => convertNumber(value),
            },
            title: {
                text: this.texts.rateDeprWorkEvolChart.yaxisTitle,
            },
        },
    ];
    public rateRepPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: false,
            hideZeroBarsWhenGrouped: true,
            dataLabels: {
                position: 'center',
                hideOverflowingLabels: false,
            },
        },
    };

    public comparisonDataLabels: ApexDataLabels = {
        enabled: false,
    };

    public grossValueRatioComparisonChartSeries: ApexAxisChartSeries = [];
    public grossValueRatioComparisonChartError: boolean = true;
    public grossValueRatioComparisonChartDataSignal: Signal<ComparisonChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(ACCOUNTING_INDICATORS_GROSS_VALUE_COMPARISON_CHART),
        ) as Signal<ComparisonChartData[]>;
    public grossValueRatioComparisonCustomTooltip: ApexTooltip['custom'];
    public grossValueRatioChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 0,
        },
    ];
    public depreciationRateComparisonChartYaxis: ApexYAxis[] = [
        {
            seriesName: 'Serie 1',
            min: 20,
            max: 70,
        },
    ];
    public grossValueRatioComparisonPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            hideZeroBarsWhenGrouped: true,
            barHeight: this.setRangeBarHeight(1),
            borderRadius: 1,
            dataLabels: {
                hideOverflowingLabels: false,
            },
        },
    };

    public grossValueChartError: boolean = true;
    public grossValueChartSeries: number[] = [];
    public grossValueLabels: string[] = this.texts.grossValueBreakdownChart.labels;
    public grossValueColors: string[] = [
        hexToRgba(colorAec9Cream, 0.9),
        hexToRgba(colorAec9Cream, 0.5),
        hexToRgba(colorAec7RedLight, 0.9),
        hexToRgba(colorAec5Brown, 0.9),
        hexToRgba(colorAec5Brown, 0.6),
        hexToRgba(colorAec5Brown, 0.3),
        hexToRgba(colorAec8RedDark, 0.3),
        hexToRgba(colorAec8RedDark, 0.6),
        hexToRgba(colorAec8RedDark, 0.9),
        hexToRgba(colorAec7RedLight, 0.9),
        hexToRgba(colorAec7RedLight, 0.5),
        hexToRgba(colorAec6Gray, 0.9),
        hexToRgba(colorAec4Orange, 0.5),
        hexToRgba(colorAec4Orange, 0.9),
        hexToRgba(colorAec2BlueDark, 0.3),
        hexToRgba(colorAec2BlueDark, 0.6),
        hexToRgba(colorAec2BlueDark, 0.9),
        hexToRgba(colorAec3Green, 0.5),
        hexToRgba(colorAec3Green, 0.9),
    ];
    public grossValueDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: (val: number) =>
            convertNumber(val, this.texts.grossValueBreakdownChart.precision) + '%',
    };
    public grossValueChartData: Signal<GrossValueChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_INDICATORS_GROSS_VALUE_BREAKDOWN_CHART),
    ) as Signal<GrossValueChartData[]>;
    public deprecRateComparisonChartSeries: ApexAxisChartSeries = [];
    public deprecRateComparisonChartError: boolean = true;
    public deprecRateComparisonChartDataSignal: Signal<ComparisonChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(
                ACCOUNTING_INDICATORS_DEPRECIATION_RATE_COMPARISON_CHART,
            ),
        ) as Signal<ComparisonChartData[]>;
    public depreciationRateComparisonCustomTooltip: ApexTooltip['custom'];
    public deprecRateComparisonPlotOptions: ApexPlotOptions = {
        bar: {
            horizontal: true,
            barHeight: this.setRangeBarHeight(1),
            borderRadius: 1,
            dataLabels: {
                hideOverflowingLabels: false,
            },
        },
    };

    public replacementValuechartData: Signal<ReplacementValueChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_REPLACEMENT_VALUE_CHART),
    ) as Signal<ReplacementValueChartData[]>;
    public replacementValueChartSeries: ApexAxisChartSeries = [];
    public replacementValueChartError: boolean = true;
    public replacementValueDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: this.setDataLabelsFormatterBySerie({ 0: null, 1: null }),
    };
    public replacementValueXaxisCategories: ApexXAxis['categories'];
    public replacementValueYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.replacementValueChart.series[0].name,
            min: 0,
            title: {
                text: this.texts.replacementValueChart.yaxisTitleLeft,
            },
        },
        {
            seriesName: this.texts.replacementValueChart.series[1].name,
            opposite: true,
            title: {
                text: this.texts.replacementValueChart.yaxisTitleRight,
            },
            stepSize: this.texts.replacementValueChart.series[1].stepSize,
        },
    ];
    public replacementValueCustomTooltip: ApexTooltip['custom'];

    public totalProvisionChartData: Signal<TotalProvisionChartData[]> = this.store.selectSignal(
        ConcessioncontrolState.getDataByKey(ACCOUNTING_TOTAL_PROVISION_CHART),
    ) as Signal<TotalProvisionChartData[]>;
    public totalProvisionChartSeries: ApexAxisChartSeries = [];
    public totalProvisionChartError: boolean = true;
    public totalProvisionDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
    };
    public totalProvisionXaxisCategories: ApexXAxis['categories'];
    public totalProvisionYaxis: ApexYAxis[] = [
        {
            seriesName: this.texts.totalProvisionChart.series[0].name,
            min: 0,
            title: {
                text: this.texts.totalProvisionChart.yaxisTitle,
            },
        },
    ];
    public totalProvisionGrid: ApexGrid = { padding: { right: 30 } };
    public totalProvisionCustomTooltip: ApexTooltip['custom'];

    public provisionsDistributionChartError: boolean = true;
    public provisionsDistributionChartSeries: number[] = [];
    public provisionsDistributionLabels: string[] = this.texts.provisionsDistributionChart.labels;
    public provisionsDistributionColors: string[] = [
        colorAec5Brown,
        colorAec7RedLight,
        colorAec8RedDark,
        colorAec3Green,
        colorAec6Gray,
        colorAec4Orange,
        colorAec2BlueDark,
        colorAec9Cream,
    ];
    public provisionsDistributionDataLabels: ApexDataLabels = {
        enabled: true,
        enabledOnSeries: [],
        formatter: (val: number) =>
            convertNumber(val, this.texts.provisionsDistributionChart.precision) + '%',
    };
    public provisionsDistributionChartData: Signal<ProvisionsDistributionChartData[]> =
        this.store.selectSignal(
            ConcessioncontrolState.getDataByKey(ACCOUNTING_PROVISIONS_DISTRIBUTION_CHART),
        ) as Signal<ProvisionsDistributionChartData[]>;

    constructor(
        private readonly route: ActivatedRoute,
        private readonly sidenavService: SidenavService,
        private readonly store: Store,
    ) {
        super(route, sidenavService, store);

        effect(() => {
            const dataList = this.chartData();
            this.chartError = this.checkForError(dataList, [
                'xYear',
                'totalValueNetBook',
                'totalDepreciation',
                'totalGrossValue',
            ]);
            if (!this.chartError) {
                this.xaxisCategories = dataList.map((data) => data.xYear);
                const totales = this.getTotales(dataList, [
                    'totalDepreciation',
                    'totalValueNetBook',
                ]);
                this.chartSeries = [
                    {
                        name: this.texts.evolutionFixedAssetsChart.totalDepreciationAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.totalDepreciation!),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.evolutionFixedAssetsChart.totalValueNetBookAxisTitle,
                        type: 'column',
                        data: dataList.map((data) => data.totalValueNetBook!),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.evolutionFixedAssetsChart.totalGrossValueAxisTitle,
                        type: 'line',
                        data: totales,
                        color: 'transparent',
                    },
                ];
                this.dataLabels.enabledOnSeries = [0, 1, 2];
            }

            const dataListEvolVb = this.chartDataEvolVb();
            this.chartErrorEvolVb = this.checkForError(dataListEvolVb, ['xYear', 'valueGrossUser']);
            if (!this.chartErrorEvolVb) {
                this.xaxisCategoriesEvolVb = dataListEvolVb.map((data) => data.xYear);
                this.chartSeriesEvolVb = [
                    {
                        name: this.texts.grossValueEvolChart.valueGrossUserAxisTitle,
                        type: 'column',
                        data: dataListEvolVb.map((data) => Math.round(data.valueGrossUser!)),
                        color: colorAec2BlueDark,
                    },
                ];
                this.dataLabelsEvolVb.enabledOnSeries = [0];
            }

            const dataListEvolRep = this.chartDataEvolRep();
            this.chartErrorEvolRep = this.checkForError(dataListEvolRep, [
                'xYear',
                'totalRateDepreWorks',
            ]);
            if (!this.chartErrorEvolRep) {
                this.xaxisCategoriesEvolRep = dataListEvolRep.map((data) => data.xYear);
                this.chartSeriesEvolRep = [
                    {
                        name: this.texts.rateDeprWorkEvolChart.totalRateDepreWorksAxisTitle,
                        type: 'column',
                        data: dataListEvolRep.map((data) => data.totalRateDepreWorks!),
                        color: colorAec3Green,
                    },
                ];
                this.dataLabelsEvolRep.enabledOnSeries = [0];
            }

            const dataListRateRep = this.chartDataRateRep();
            this.chartErrorRateRep = this.checkForError(dataListRateRep, [
                'xYear',
                'rateDepreciationMv',
                'rateDepreciationLv',
                'rateDepreciationSubstation',
                'rateDepreciationTransformer',
                'rateDepreciationLinky',
                'rateDepreciationMarket',
                'rateDepreciationCollectiveWorks',
                'rateDepreciationOtherOl',
                'rateDepreciationOtherOnl',
                'rateDepreciationUndergroundLr',
                'rateDepreciationBreaker',
                'rateDepreciationOverhead',
                'rateDepreciationUndergroundDi',
            ]);
            if (!this.chartErrorRateRep) {
                this.xaxisCategoriesRateRep = [
                    'HTA',
                    'BT',
                    'Postes HTA/BT',
                    'Transformateurs',
                    'Compteurs Linky',
                    ['Compteurs', 'marché d’affaires'],
                    ['Ouvrages collectifs', 'de branchement'],
                    'Autres OL',
                    'Autres ONL',
                    'LR sout et aérosout',
                    'Disjoncteurs',
                    'LR et DI aériens',
                    'DI sout et aérosout',
                ];
                const keysToRemove: (keyof ChartDataRateRep)[] = [
                    'id',
                    'year',
                    'xYear',
                    'organizationId',
                ];

                const formatChartData = (data: ChartDataRateRep) => {
                    const formattedData = { ...data };
                    keysToRemove.forEach((key) => delete formattedData[key]);

                    return Object.values(formattedData)
                        .filter((value) => typeof value === 'number')
                        .map((value) => value as number);
                };

                dataListRateRep.sort((a, b) => a.xYear! - b.xYear!);
                this.chartSeriesRateRep = [
                    {
                        name: this.texts.rateDeprWorksFamilyChart.yearPreviousAxisTitle,
                        data: formatChartData(dataListRateRep[0]),
                        color: colorAec7RedLight,
                    },
                    {
                        name: this.texts.rateDeprWorksFamilyChart.yearCurrentAxisTitle,
                        data: formatChartData(dataListRateRep[1]),
                        color: colorAec8RedDark,
                    },
                ];
                this.dataLabelsRateRep.enabledOnSeries = [0, 1];
            }

            const grossValueRatioComparisonChartData =
                this.grossValueRatioComparisonChartDataSignal();
            this.grossValueRatioComparisonChartError = this.checkForError(
                grossValueRatioComparisonChartData,
                ['category', 'min', 'max', 'mean'],
            );
            if (!this.grossValueRatioComparisonChartError) {
                this.grossValueRatioComparisonChartSeries = [
                    {
                        name: this.texts.grossValueRatioComparisonChart.serieLabel,
                        data: grossValueRatioComparisonChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.grossValueRatioComparisonChart.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },
                                {
                                    name: this.texts.grossValueRatioComparisonChart
                                        .clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.grossValueRatioComparisonChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.grossValueRatioComparisonChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }

            const grossValueChartData = this.grossValueChartData();
            this.grossValueChartError = this.checkForError(grossValueChartData, [
                'othersNonLocated',
                'otherLocatedAssets',
                'circuitBreakers',
                'businessMarketMeters',
                'linkyMeters',
                'nonLocatedMetering',
                'aerialUndergroundDi',
                'aerialUndergroundLr',
                'aerialConnections',
                'ocb',
                'nonLocatedConnectionFacilities',
                'transformers',
                'linkyConcentrators',
                'lvMvSubstations',
                'undergroundLv',
                'twistedLv',
                'aerialLv',
                'undergroundMv',
                'aerialMv',
            ]);
            if (!this.grossValueChartError) {
                this.grossValueChartSeries = [
                    grossValueChartData[0].othersNonLocated || 0,
                    grossValueChartData[0].otherLocatedAssets || 0,
                    grossValueChartData[0].circuitBreakers || 0,
                    grossValueChartData[0].businessMarketMeters || 0,
                    grossValueChartData[0].linkyMeters || 0,
                    grossValueChartData[0].nonLocatedMetering || 0,
                    grossValueChartData[0].aerialUndergroundDi || 0,
                    grossValueChartData[0].aerialUndergroundLr || 0,
                    grossValueChartData[0].aerialConnections || 0,
                    grossValueChartData[0].ocb || 0,
                    grossValueChartData[0].nonLocatedConnectionFacilities || 0,
                    grossValueChartData[0].transformers || 0,
                    grossValueChartData[0].linkyConcentrators || 0,
                    grossValueChartData[0].lvMvSubstations || 0,
                    grossValueChartData[0].undergroundLv || 0,
                    grossValueChartData[0].twistedLv || 0,
                    grossValueChartData[0].aerialLv || 0,
                    grossValueChartData[0].undergroundMv || 0,
                    grossValueChartData[0].aerialMv || 0,
                ];
                this.grossValueDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5, 6, 7];
            }

            const deprecRateComparisonChartData = this.deprecRateComparisonChartDataSignal();
            this.deprecRateComparisonChartError = this.checkForError(
                deprecRateComparisonChartData,
                ['category', 'min', 'max', 'mean'],
            );
            if (!this.deprecRateComparisonChartError) {
                this.deprecRateComparisonChartSeries = [
                    {
                        name: this.texts.depreciationRateComparisonChart.serieLabel,
                        data: deprecRateComparisonChartData.map((data) => ({
                            x: data.category,
                            y: [data.min, data.max],
                            goals: [
                                {
                                    name: this.texts.depreciationRateComparisonChart.aecMeanLabel,
                                    value: data.mean!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec8RedDark,
                                },

                                {
                                    name: this.texts.depreciationRateComparisonChart
                                        .clientValueLabel,
                                    value: data.clientValue!,
                                    strokeWidth: 10,
                                    strokeHeight: 0,
                                    strokeLineCap: StrokeLineCap.ROUND,
                                    strokeColor: colorAec1BlueLight,
                                },
                            ],
                        })),
                        color: colorAec4Orange,
                    },
                    {
                        name: this.texts.depreciationRateComparisonChart.serieLabelAvg,
                        type: 'bar',
                        data: [],
                        color: colorAec8RedDark,
                    },
                    {
                        name: this.texts.depreciationRateComparisonChart.serieLabelValue,
                        type: 'bar',
                        data: [],
                        color: colorAec1BlueLight,
                    },
                ];
            }

            const replacementValuechartData = this.replacementValuechartData();
            this.replacementValueChartError = this.checkForError(replacementValuechartData, [
                'xYear',
                'replacementValue',
                'annualReplacementCoeff',
            ]);
            if (!this.replacementValueChartError) {
                const updatedYaxis = [...this.replacementValueYaxis];
                const minCoeff = Math.min(
                    ...this.getLastValues(replacementValuechartData, 5).map(
                        (data) => data.annualReplacementCoeff!,
                    ),
                );
                updatedYaxis[1].min = Math.floor(minCoeff) - 1;

                const maxCoeff = Math.max(
                    ...this.getLastValues(replacementValuechartData, 5).map(
                        (data) => data.annualReplacementCoeff!,
                    ),
                );
                updatedYaxis[1].max = Math.ceil(maxCoeff) + 1;
                this.replacementValueYaxis = updatedYaxis;

                this.replacementValueXaxisCategories = this.getLastValues(
                    replacementValuechartData,
                    5,
                ).map((data) => data.xYear);
                this.replacementValueChartSeries = [
                    {
                        name: this.texts.replacementValueChart.series[0].name,
                        type: 'column',
                        data: this.getLastValues(replacementValuechartData, 5).map(
                            (data) => data.replacementValue!,
                        ),
                        color: colorAec1BlueLight,
                    },
                    {
                        name: this.texts.replacementValueChart.series[1].name,
                        type: 'line',
                        data: this.getLastValues(replacementValuechartData, 5).map(
                            (data) => data.annualReplacementCoeff!,
                        ),
                        color: colorAec7RedLight,
                    },
                ];
                this.replacementValueDataLabels.enabledOnSeries = [0, 1];
            }

            const totalProvisionChartData = this.totalProvisionChartData();
            this.totalProvisionChartError = this.checkForError(totalProvisionChartData, [
                'xYear',
                'totalProvision',
            ]);
            if (!this.totalProvisionChartError) {
                this.totalProvisionXaxisCategories = this.getLastValues(
                    totalProvisionChartData,
                    5,
                ).map((data) => data.xYear);

                this.totalProvisionChartSeries = [
                    {
                        name: this.texts.totalProvisionChart.series[0].name,
                        type: 'column',
                        data: this.getLastValues(totalProvisionChartData, 5).map(
                            (data) => data.totalProvision!,
                        ),
                        color: colorAec7RedLight,
                    },
                ];
                this.totalProvisionDataLabels.enabledOnSeries = [0];
            }

            const provisionsDistributionChartData = this.provisionsDistributionChartData();
            this.provisionsDistributionChartError = this.checkForError(
                provisionsDistributionChartData,
                [
                    'meters',
                    'collectiveConnection',
                    'individualConnection',
                    'lv',
                    'transformers',
                    'posts',
                    'mv',
                    'others',
                ],
            );
            if (!this.provisionsDistributionChartError) {
                this.provisionsDistributionChartSeries = [
                    provisionsDistributionChartData[0].meters || 0,
                    provisionsDistributionChartData[0].collectiveConnection || 0,
                    provisionsDistributionChartData[0].individualConnection || 0,
                    provisionsDistributionChartData[0].mv || 0,
                    provisionsDistributionChartData[0].transformers || 0,
                    provisionsDistributionChartData[0].posts || 0,
                    provisionsDistributionChartData[0].lv || 0,
                    provisionsDistributionChartData[0].others || 0,
                ];
                this.provisionsDistributionDataLabels.enabledOnSeries = [0, 1, 2, 3, 4, 5, 6, 7];
            }
        });
    }

    ngOnInit() {
        // Every time currentOrganization or currentYear changes, we tell the store he has to ask for the data
        combineLatest([this._currentOrganization$, this._currentYear$])
            .pipe(untilDestroyed(this), distinctUntilChanged())
            .subscribe(([currentOrganization, currentYear]) => {
                this.store.dispatch(new PrepareDataFromKeys({ keys: KEYS }));

                const yearCurrentAxisTitle =
                    this.texts.rateDeprWorksFamilyChart.yearCurrentAxisTitle;
                this.texts.rateDeprWorksFamilyChart.yearCurrentAxisTitle =
                    this.setrateDeprWorksFamilyChartTitle(yearCurrentAxisTitle, currentYear);
                const yearPreviousAxisTitle =
                    this.texts.rateDeprWorksFamilyChart.yearPreviousAxisTitle;
                this.texts.rateDeprWorksFamilyChart.yearPreviousAxisTitle =
                    this.setrateDeprWorksFamilyChartTitle(yearPreviousAxisTitle, currentYear);
            });

        this.evolutionFixedAssetsCustomTooltip = this.setEvolutionFixedAssetsCustomTooltip();

        this.grossValueRatioComparisonCustomTooltip = this.setChartCustomTooltip(
            this.texts.grossValueRatioComparisonChart.precision,
            this.texts.grossValueRatioComparisonChart.unitTooltip,
        );
        this.depreciationRateComparisonCustomTooltip = this.setChartCustomTooltip(
            this.texts.depreciationRateComparisonChart.precision,
            this.texts.depreciationRateComparisonChart.unitTooltip,
        );

        this.replacementValueCustomTooltip = this.setReplacementValueCustomTooltip();
        this.totalProvisionCustomTooltip = this.setTotalProvisionCustomTooltip();
    }

    public setChartCustomTooltip(
        precision: number | null = null,
        unit: string,
    ): ApexTooltip['custom'] {
        return function ({ series, seriesIndex, dataPointIndex, w }) {
            const dataSerie = w.globals.initialSeries[seriesIndex];
            const serieName = dataSerie.name;
            const serieColor = dataSerie.color;
            const dataPoint = dataSerie.data[dataPointIndex];
            const min = convertNumber(dataPoint.y[0], precision, false);
            const max = convertNumber(dataPoint.y[1], precision, false);

            const meanGoal = dataPoint.goals[0];
            const meanValue = convertNumber(meanGoal.value, precision, false);
            const meanName = meanGoal.name;
            const meanColor = meanGoal.strokeColor;

            const clientValueGoal = dataPoint.goals[1];
            const clientValue = convertNumber(clientValueGoal.value, precision, false);
            const clientValueName = clientValueGoal.name;
            const clientColor = clientValueGoal.strokeColor;

            return `
                <div class="apexcharts-tooltip-title">
                    ${dataPoint.x}
                </div>

                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${serieColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${serieName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${min} - ${max} ${unit}</span>
                        </div>
                         <div class="apexcharts-tooltip-goals-group">
                         </div>
                    </div>
                </div>
                <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 2;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${meanColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${meanName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${meanValue} ${unit}</span>
                        </div>
                         <div class="apexcharts-tooltip-goals-group">
                         </div>
                    </div>
                </div>
                 <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 3;display: flex;">
                    <span class="apexcharts-tooltip-marker" style="background-color: ${clientColor}"></span>
                    <div class="apexcharts-tooltip-text">
                        <div class="apexcharts-tooltip-y-group">
                            <span class="apexcharts-tooltip-text-y-label">${clientValueName}:</span>
                            <span class="apexcharts-tooltip-text-y-value"> ${clientValue} ${unit}</span>
                        </div>
                    </div>
                </div>
            `;
        };
    }

    private setrateDeprWorksFamilyChartTitle(rawTitle: string, currentYear: number): string {
        return rawTitle
            .replace('N-1', (currentYear - 1).toString())
            .replace('N', currentYear.toString());
    }

    private setEvolutionFixedAssetsCustomTooltip(): ApexTooltip['custom'] {
        return ({ data, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.evolutionFixedAssetsChart.precision;

            const totalDepreciationSerie = w.globals.initialSeries[0];
            const {
                name: totalDepreciationName,
                color: totalDepreciationColor,
                valueStringified: totalDepreciationStringified,
            } = this.setCustomTooltipData(precision, totalDepreciationSerie, dataPointIndex);

            const totalValueNetBookSerie = w.globals.initialSeries[1];
            const {
                name: totalValueNetBookName,
                color: totalValueNetBookColor,
                valueStringified: totalValueNetBookStringified,
            } = this.setCustomTooltipData(precision, totalValueNetBookSerie, dataPointIndex);

            const totalesSerie = w.globals.initialSeries[2];
            const {
                name: totalesName,
                color: totalesColor,
                valueStringified: totalesStringified,
            } = this.setCustomTooltipData(precision, totalesSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: totalDepreciationColor,
                    name: totalDepreciationName,
                    value: totalDepreciationStringified,
                },
                {
                    color: totalValueNetBookColor,
                    name: totalValueNetBookName,
                    value: totalValueNetBookStringified,
                },
                {
                    color: colorBlack,
                    name: totalesName,
                    value: totalesStringified,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setReplacementValueCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.replacementValueChart.series[0].precision;
            const replacementValueSerie = w.globals.initialSeries[0];
            const {
                name: replacementValueName,
                color: replacementValueColor,
                valueStringified: replacementValueStringified,
            } = this.setCustomTooltipData(precision, replacementValueSerie, dataPointIndex);

            const coeffPrecision = this.texts.replacementValueChart.series[1].precision;
            const coeffSerie = w.globals.initialSeries[1];
            const {
                name: coeffName,
                color: coeffColor,
                valueStringified: coeffPointStringified,
            } = this.setCustomTooltipData(coeffPrecision, coeffSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: replacementValueColor,
                    name: this.texts.replacementValueChart.series[0].tooltipName,
                    value: `${replacementValueStringified} ${this.texts.replacementValueChart.series[0].unit}`,
                },
                {
                    color: coeffColor,
                    name: this.texts.replacementValueChart.series[1].tooltipName,
                    value: `${coeffPointStringified} ${this.texts.replacementValueChart.series[1].unit}`,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }

    private setTotalProvisionCustomTooltip(): ApexTooltip['custom'] {
        return ({ series, seriesIndex, dataPointIndex, w }) => {
            const collapsedSeriesIndices = w.globals.collapsedSeriesIndices;

            const categoryLabels = w.globals.categoryLabels;
            const title = categoryLabels[dataPointIndex];

            const precision = this.texts.totalProvisionChart.series[0].precision;
            const valueSerie = w.globals.initialSeries[0];
            const {
                name: name,
                color: color,
                valueStringified: valueStringified,
            } = this.setCustomTooltipData(precision, valueSerie, dataPointIndex);

            const dataTooltips = [
                {
                    color: color,
                    name: this.texts.totalProvisionChart.series[0].tooltipName,
                    value: `${valueStringified} ${this.texts.totalProvisionChart.series[0].unit}`,
                },
            ];
            const html = this.setCustomTooltip(collapsedSeriesIndices, title, dataTooltips);
            return html;
        };
    }
}
